import { pipe } from 'fp-ts/lib/function';
import { FC, ReactNode, createContext, useEffect, useReducer } from 'react';

import { fetchLotPrices } from '@/shared/api/buyer';
import {
  addUserInterest,
  deleteUserInterest,
  fetchUserFavorites,
} from '@/shared/api/user';
import { isLoggedIn } from '@/shared/cookies';
import { nullableData } from '@/shared/utils/http';
import { isInBuilder } from '@/shared/utils/isInBuilder';

export enum LoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
  INIT = 'init',
}

interface LotFavorite {
  lotId: number;
  count: number;
  isCurrentUserFavorite: boolean;
}

const loadFavoriteAction = () => ({
  type: 'load' as const,
});
type LoadFavoriteAction = ReturnType<typeof loadFavoriteAction>;

const initUserFavoriteAction = (favorites: LotFavorite[]) => ({
  type: 'init' as const,
  favorites,
});
type InitUserFavoriteAction = ReturnType<typeof initUserFavoriteAction>;

const addFavoriteAction = (lotId: number) => ({
  type: 'add' as const,
  lotId,
});
type AddFavoriteAction = ReturnType<typeof addFavoriteAction>;

const deleteFavoriteAction = (lotId: number) => ({
  type: 'delete' as const,
  lotId,
});
type DeleteFavoriteAction = ReturnType<typeof deleteFavoriteAction>;

const cancelFavoriteAction = () => ({
  type: 'cancel' as const,
});
type CancelFavoriteAction = ReturnType<typeof cancelFavoriteAction>;

interface UserLotFavoritesState {
  lotFavorites: {
    [key in number]: LotFavorite;
  };
  state: LoadingState;
}

interface UserLotFavoritesContext extends UserLotFavoritesState {
  addFavorite?: (lotId: number) => Promise<void>;
  deleteFavorite?: (lotId: number) => Promise<void>;
}

const initialState: UserLotFavoritesState = {
  lotFavorites: [],
  state: LoadingState.INIT,
};

export const UserLotFavoritesContext =
  createContext<UserLotFavoritesContext>(initialState);

interface Props {
  lotIds: number[];
  children: ReactNode;
}

type Actions =
  | LoadFavoriteAction
  | InitUserFavoriteAction
  | AddFavoriteAction
  | DeleteFavoriteAction
  | CancelFavoriteAction;

export const UserLotFavoritesProvider: FC<Props> = ({ lotIds, children }) => {
  const [favoritesState, dispatch] = useReducer<
    UserLotFavoritesState,
    [Actions]
  >((previousState, action) => {
    switch (action.type) {
      case 'load':
        return { ...previousState, state: LoadingState.LOADING };
      case 'init': {
        const lotFavorites = action.favorites.reduce(
          (result, item) => ({
            ...result,
            [item.lotId]: item,
          }),
          {}
        );

        return {
          ...previousState,
          state: LoadingState.IDLE,
          lotFavorites,
        };
      }
      case 'add': {
        const lotFavorites = previousState.lotFavorites;
        if (!lotFavorites[action.lotId]) {
          lotFavorites[action.lotId].count = 0;
        }
        lotFavorites[action.lotId].count += 1;
        lotFavorites[action.lotId].isCurrentUserFavorite = true;
        return {
          ...previousState,
          state: LoadingState.IDLE,
          lotFavorites,
        };
      }
      case 'delete': {
        const lotFavorites = previousState.lotFavorites;
        if (!lotFavorites[action.lotId]) {
          return {
            ...previousState,
            state: LoadingState.IDLE,
          };
        }

        lotFavorites[action.lotId].count -= 1;
        lotFavorites[action.lotId].isCurrentUserFavorite = false;
        return {
          ...previousState,
          state: LoadingState.IDLE,
          lotFavorites,
        };
      }
      case 'cancel': {
        return {
          ...previousState,
          state: LoadingState.IDLE,
        };
      }
      default:
        return previousState;
    }
  }, initialState);

  const addFavorite = async (lotId: number) => {
    if (!isLoggedIn()) return;
    if (favoritesState.lotFavorites[lotId].isCurrentUserFavorite) return;

    dispatch(loadFavoriteAction());
    try {
      await addUserInterest(lotId);
      dispatch(addFavoriteAction(lotId));
    } catch {
      dispatch(cancelFavoriteAction());
    }
  };

  const deleteFavorite = async (lotId: number) => {
    if (!isLoggedIn()) return;
    if (!favoritesState.lotFavorites[lotId].isCurrentUserFavorite) return;

    dispatch(loadFavoriteAction());
    try {
      await deleteUserInterest(lotId);
      dispatch(deleteFavoriteAction(lotId));
    } catch {
      dispatch(cancelFavoriteAction());
    }
  };

  useEffect(() => {
    (async () => {
      // Disable all calls inside builder.io
      if (isInBuilder()) {
        dispatch(
          initUserFavoriteAction(
            lotIds.map((lotId) => ({
              lotId,
              count: 10,
              isCurrentUserFavorite: false,
            }))
          )
        );
        return;
      }

      if (!isLoggedIn()) {
        const eitherLotPrices = await fetchLotPrices(lotIds);
        const prices = pipe(eitherLotPrices, nullableData) ?? [];
        const initFavorites = prices.map((price) => ({
          lotId: price.id,
          count: price.favorite_count,
          isCurrentUserFavorite: false,
        }));
        dispatch(initUserFavoriteAction(initFavorites));
        return;
      }

      const [eitherFavorites, eitherLotPrices] = await Promise.all([
        fetchUserFavorites(lotIds),
        fetchLotPrices(lotIds),
      ]);

      const favorites = pipe(eitherFavorites, nullableData) ?? [];
      const prices = pipe(eitherLotPrices, nullableData) ?? [];
      const initFavorites = prices.map((price) => ({
        lotId: price.id,
        count: price.favorite_count,
        isCurrentUserFavorite: Boolean(
          favorites.find((item) => item.lot_id === price.id)
        ),
      }));
      dispatch(initUserFavoriteAction(initFavorites));
    })();
  }, [lotIds]);

  return (
    <UserLotFavoritesContext.Provider
      value={{ ...favoritesState, addFavorite, deleteFavorite }}
    >
      {children}
    </UserLotFavoritesContext.Provider>
  );
};
